import axios, { AxiosInstance, AxiosResponse, AxiosRequestConfig } from 'axios';
import Auth from '../AuthMiddleware/Auth';
import { Config } from '../Config';
import { logger } from '../utils/Logger';

export type QueryParams = {
  [x: string]: string | number | boolean | null | undefined;
};

export class BaseApiClient {
  public baseUrl: string;
  protected readonly instance: AxiosInstance;
  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    this.instance = axios.create({
      baseURL: baseUrl,
    });
  }
  static toQueryString(params: QueryParams = {}) {
    const query = Object.keys(params).map((key: string) => {
      if (key in params) {
        const value = params[key];
        if (value !== undefined) {
          return `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`;
        }
      }
      return undefined;
    });
    const queryString = query.filter(Boolean).join('&');
    return queryString ? `?${queryString}` : '';
  }

  static findBaseUrl(): string {
    return Config.API_URL;
  }

  async post<T>(
    path: string,
    body: any,
    axiosConfig: AxiosRequestConfig = {}
  ): Promise<T> {
    try {
      const url = `${this.baseUrl}${path}`;
      // eslint-disable-next-line no-console
      logger.debug(`POST request... ${url} ${body}`);
      const token = await Auth.getTokenAsync(this.baseUrl);
      const header = Auth.setHeader(token);

      axiosConfig.headers = header;
      const response: AxiosResponse<T> = await this.instance.post<T>(
        url,
        body,
        { ...axiosConfig }
      );
      // eslint-disable-next-line no-console
      logger.debug(
        `POST result: ${response.status} ${response.statusText} ${response.data}`
      );
      return response.data;
    } catch (e) {
      if (process.env.NODE_ENV !== 'test') {
        // eslint-disable-next-line no-console
        logger.error(`POST request error ${e} ${path}`);
      }
      throw e;
    }
  }

  async put<T>(path: string, body: any): Promise<T> {
    try {
      const url = `${this.baseUrl}${path}`;
      // eslint-disable-next-line no-console
      logger.debug(`PUT request... ${url} ${body}`);
      const token = await Auth.getTokenAsync(this.baseUrl);
      const header = Auth.setHeader(token);
      const response: AxiosResponse<T> = await this.instance.put<T>(url, body, {
        headers: header,
      });
      // eslint-disable-next-line no-console
      logger.debug(
        `Put result:
        ${response.status}
        ${response.statusText}
        ${response.data}`
      );
      return response.data;
    } catch (e) {
      if (process.env.NODE_ENV !== 'test') {
        // eslint-disable-next-line no-console
        logger.error(`Put request error ${e} ${path}`);
      }
      throw e;
    }
  }

  async get<T>(path: string, queryParams: QueryParams = {}): Promise<T> {
    try {
      const queryString = BaseApiClient.toQueryString(queryParams);
      const url = `${this.baseUrl}${path}${queryString}`;
      const token = await Auth.getTokenAsync(this.baseUrl);
      const header = Auth.setHeader(token);
      const response: AxiosResponse<T> = await this.instance.get<T>(url, {
        headers: header,
      });
      return response.data;
    } catch (e: any) {
      // eslint-disable-next-line no-console
      logger.error(`GET request error ${e} ${path}`);
      throw e;
    }
  }
  async delete<T>(path: string, body?: any): Promise<T> {
    try {
      const url = `${this.baseUrl}${path}`;
      // eslint-disable-next-line no-console
      logger.debug(`DELETE request... ${url} ${body}`);
      const token = await Auth.getTokenAsync(this.baseUrl);
      const header = Auth.setHeader(token);
      const config = body
        ? { data: body, headers: header }
        : { headers: header };
      const response: AxiosResponse<T> = await this.instance.delete<T>(
        url,
        config
      );
      // eslint-disable-next-line no-console
      logger.debug(
        `DELETE result:
        ${response.status}
        ${response.statusText}
        ${response.data}`
      );
      return response.data;
    } catch (e) {
      if (process.env.NODE_ENV !== 'test') {
        // eslint-disable-next-line no-console
        logger.error(`Delete request error ${e} ${path}`);
      }
      throw e;
    }
  }
}
